/*
  Free gift picker (cart drawer).

  Gift lines added by the picker carry the property `_free_gift_picker: "true"`.
  The picker shows when a trigger discount (Theme settings > Free gift) is applied to the
  cart OR the current time is inside the date range, and nothing excludes it (excluded page
  visited, excluded variant in the cart). Picker gift lines are removed when it is not active.

  Excluded pages (landing pages with their own free gift): products added to the cart while
  on one are recorded in the hidden cart attribute `_extra_gift_excluded` as
  "<page path>|<variant id>,<variant id>". While any of those products is still in the cart the
  picker stays hidden and picker gift lines are removed, on every page. Once they are all gone
  the exclusion lapses and the attribute is cleared. Detection works by comparing cart
  quantities before and after, so it does not matter how the landing page adds to cart.

  How many gifts a cart earns (1 per item, 3 for a 3-month supply, capped) is worked out in Liquid by
  snippets/free-gift-picker-allowance.liquid, which needs variant metafields. This script reads it from
  sections/free-gift-allowance.liquid and removes the newest picker gifts beyond it, e.g. when a
  3-month box is swapped for a 1-month one.

  Pure cart logic lives on window.FreeGiftPicker so it can be unit-tested in Node
  (tests/free-gift-picker.test.mjs). DOM code only runs in a browser.
*/
(() => {
  if (window.FreeGiftPicker) return;

  const PROPERTY = '_free_gift_picker';
  const norm = (value) => String(value || '').trim().toLowerCase();

  function parseTriggers(text) {
    const seen = new Set();
    String(text || '')
      .split('\n')
      .map(norm)
      .forEach((trigger) => trigger && seen.add(trigger));
    return [...seen];
  }

  function activeDiscountTitles(cart) {
    const titles = new Set();
    (cart.cart_level_discount_applications || []).forEach((app) => titles.add(norm(app.title)));
    (cart.items || []).forEach((item) => {
      (item.line_level_discount_allocations || []).forEach((alloc) => {
        titles.add(norm(alloc.discount_application && alloc.discount_application.title));
      });
    });
    titles.delete('');
    return [...titles];
  }

  function isTriggerActive(cart, triggers) {
    if (!triggers.length) return false;
    return activeDiscountTitles(cart).some((title) => triggers.includes(title));
  }

  const EXCLUDED_ATTRIBUTE = '_extra_gift_excluded';
  const LEGACY_EXCLUDED_ATTRIBUTE = '_free_gift_excluded';
  const isGiftLine = (item) => Boolean(item.properties && (item.properties[PROPERTY] || item.properties._Gifted));

  function normalizePath(path, root) {
    let clean = String(path || '').trim().toLowerCase().split(/[?#]/)[0];
    const prefix = String(root || '/').toLowerCase().replace(/\/+$/, '');
    if (prefix && (clean === prefix || clean.startsWith(`${prefix}/`))) clean = clean.slice(prefix.length);
    clean = clean.replace(/\/+$/, '');
    return clean.startsWith('/') ? clean : `/${clean}`;
  }

  function parsePaths(text) {
    const seen = new Set();
    String(text || '')
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean)
      .forEach((line) => seen.add(normalizePath(line.replace(/^[a-z][a-z0-9+.-]*:\/\/[^/]*/i, ''), '/')));
    return [...seen];
  }

  function isExcludedPath(pathname, excluded, root) {
    return excluded.includes(normalizePath(pathname, root));
  }

  function parseExclusion(value) {
    const text = String(value || '').trim();
    if (!text) return null;
    const bar = text.lastIndexOf('|');
    const path = bar === -1 ? '' : text.slice(0, bar);
    const ids = (bar === -1 ? text : text.slice(bar + 1)).split(',').map((id) => id.trim()).filter((id) => /^\d+$/.test(id));
    return { path, variantIds: [...new Set(ids)] };
  }

  function formatExclusion(path, variantIds) {
    return `${path}|${variantIds.join(',')}`;
  }

  const exclusionOf = (cart) => parseExclusion(cart && cart.attributes && cart.attributes[EXCLUDED_ATTRIBUTE]);

  /* True while a product added on an excluded page is still in the cart (gift lines don't count). */
  function isExcluded(cart) {
    const exclusion = exclusionOf(cart);
    if (!exclusion || !exclusion.variantIds.length) return false;
    return (cart.items || []).some((item) => !isGiftLine(item) && exclusion.variantIds.includes(String(item.variant_id)));
  }

  function variantQuantities(cart) {
    const quantities = {};
    (cart.items || []).forEach((item) => {
      if (isGiftLine(item)) return;
      const id = String(item.variant_id);
      quantities[id] = (quantities[id] || 0) + item.quantity;
    });
    return quantities;
  }

  /* "variant:qty,variant:qty" rendered by layout/theme.liquid (gift lines left out). */
  function parseCartLines(text) {
    const quantities = {};
    String(text || '')
      .split(',')
      .forEach((pair) => {
        const match = pair.trim().match(/^(\d+):(\d+)$/);
        if (match) quantities[match[1]] = (quantities[match[1]] || 0) + Number(match[2]);
      });
    return quantities;
  }

  function addedVariants(before, cart) {
    const after = variantQuantities(cart);
    return Object.keys(after).filter((id) => after[id] > (before[id] || 0));
  }

  /* The new attribute value, or null when nothing new needs recording. The first page is kept. */
  function nextExclusionValue(cart, path, added) {
    const existing = exclusionOf(cart);
    const ids = existing ? existing.variantIds.slice() : [];
    const fresh = added.filter((id) => !ids.includes(id));
    if (!fresh.length) return null;
    return formatExclusion((existing && existing.path) || path, ids.concat(fresh));
  }

  /* Attributes to blank out: a lapsed exclusion, and the pre-rename attribute. */
  function attributesToClear(cart) {
    const attributes = (cart && cart.attributes) || {};
    const clear = {};
    if (attributes[EXCLUDED_ATTRIBUTE] && !isExcluded(cart)) clear[EXCLUDED_ATTRIBUTE] = '';
    if (attributes[LEGACY_EXCLUDED_ATTRIBUTE]) clear[LEGACY_EXCLUDED_ATTRIBUTE] = '';
    return Object.keys(clear).length ? clear : null;
  }

  /* window: { start, end } in Unix seconds, 0 = open-ended. Inactive when neither bound is set. */
  function inWindow(window, now) {
    if (!window || (!window.start && !window.end)) return false;
    if (window.start && now < window.start) return false;
    if (window.end && now > window.end) return false;
    return true;
  }

  function parseVariantIds(text) {
    const seen = new Set();
    String(text || '')
      .split('\n')
      .forEach((line) => {
        const match = line.trim().match(/^(?:gid:\/\/shopify\/ProductVariant\/)?(\d+)$/i);
        if (match) seen.add(match[1]);
      });
    return [...seen];
  }

  function hasExcludedVariant(cart, excludedVariants) {
    if (!excludedVariants || !excludedVariants.length) return false;
    return (cart.items || []).some((item) => excludedVariants.includes(String(item.variant_id)));
  }

  /* rules: { enabled, triggers, window, excludedVariants }. The JS twin of snippets/free-gift-picker-active.liquid. */
  function isPickerActive(cart, rules, now) {
    if (rules.enabled === false) return false;
    const triggered = isTriggerActive(cart, rules.triggers || []) || inWindow(rules.window, now);
    return triggered && !isExcluded(cart) && !hasExcludedVariant(cart, rules.excludedVariants);
  }

  function pickerGiftLines(cart) {
    return (cart.items || []).filter((item) => item.properties && item.properties[PROPERTY]);
  }

  /*
    `tried` holds keys whose removal already failed, so a failing request is not retried on
    every cart change. Line keys are deterministic (variant + properties), so a gift re-picked
    later has the same key: an active trigger therefore clears `tried`.
  */
  function keysToRemove(cart, rules, tried, now) {
    if (isPickerActive(cart, rules, now)) {
      tried.clear();
      return [];
    }
    return pickerGiftLines(cart)
      .map((item) => item.key)
      .filter((key) => !tried.has(key));
  }

  /*
    Picker gift lines beyond the cart's allowance, newest first (cart.js lists the newest line first),
    so the gifts chosen first are kept. Keys already tried are skipped, as in keysToRemove.
  */
  function excessGiftKeys(cart, allowance, tried) {
    const lines = pickerGiftLines(cart);
    let extra = lines.reduce((sum, item) => sum + item.quantity, 0) - Math.max(0, allowance);
    const keys = [];
    for (const item of lines) {
      if (extra <= 0) break;
      if (tried && tried.has(item.key)) continue;
      keys.push({ key: item.key, quantity: Math.max(0, item.quantity - extra) });
      extra -= item.quantity;
    }
    return keys;
  }

  /*
    Slide-in style: gifts an add-to-cart earned = how much each line's units (snippets/free-gift-line-units.liquid,
    {"<line key>": units}) went up since the last snapshot. Lines that are new or grew count; removed lines don't.
  */
  function earnedByAdd(before, after) {
    return Object.keys(after || {}).reduce((sum, key) => sum + Math.max(0, (after[key] || 0) - ((before || {})[key] || 0)), 0);
  }

  /* Gifts to offer: what the add earned, but never more than the cart still has room for. */
  function modalGiftCount(earned, allowance, claimed) {
    return Math.max(0, Math.min(earned, allowance - claimed));
  }

  /* Discount codes are applied and removed through cart/update, so it re-arms the watcher too. */
  const CART_MUTATION = /\/cart\/(add|change|update|clear)(\.js)?$/;
  function rearmsWatcher(pathname) {
    const match = pathname.match(CART_MUTATION);
    return Boolean(match && (match[1] === 'add' || match[1] === 'update'));
  }

  const api = {
    PROPERTY,
    EXCLUDED_ATTRIBUTE,
    parseTriggers,
    activeDiscountTitles,
    isTriggerActive,
    inWindow,
    parseVariantIds,
    hasExcludedVariant,
    isPickerActive,
    normalizePath,
    parsePaths,
    isExcludedPath,
    parseExclusion,
    formatExclusion,
    isExcluded,
    variantQuantities,
    parseCartLines,
    addedVariants,
    nextExclusionValue,
    attributesToClear,
    pickerGiftLines,
    keysToRemove,
    excessGiftKeys,
    earnedByAdd,
    modalGiftCount,
    rearmsWatcher,
  };
  window.FreeGiftPicker = api;

  if (typeof document === 'undefined') return;

  const script = document.currentScript;
  let triggers = [];
  try {
    triggers = parseTriggers(JSON.parse((script && script.dataset.triggers) || '""'));
  } catch (error) {
    console.warn('[free-gift-picker] could not read trigger discounts', error);
  }
  const numberData = (name) => Number((script && script.dataset[name]) || 0) || 0;
  let excludedVariants = [];
  try {
    excludedVariants = parseVariantIds(JSON.parse((script && script.dataset.excludedVariants) || '""'));
  } catch (error) {
    console.warn('[free-gift-picker] could not read excluded variants', error);
  }
  /* Window bounds are computed in Liquid (store timezone) by snippets/free-gift-picker-window.liquid. */
  /* Theme settings > Free gift > Enable. While off, the only job left is removing leftover picker gifts. */
  const enabled = !script || script.dataset.enabled !== 'false';
  const rules = {
    enabled,
    triggers,
    window: { start: numberData('windowStart'), end: numberData('windowEnd') },
    excludedVariants,
  };
  const nowSeconds = () => Math.floor(Date.now() / 1000);
  let excludedPaths = [];
  try {
    excludedPaths = parsePaths(JSON.parse((script && script.dataset.excludedPages) || '""'));
  } catch (error) {
    console.warn('[free-gift-picker] could not read excluded pages', error);
  }
  let mayHaveGifts = !script || script.dataset.hasGifts !== 'false';
  const root = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const cartDrawer = () => document.querySelector('cart-drawer');
  const drawerSections = () => {
    const drawer = cartDrawer();
    return (drawer && drawer.getSectionsToRender && drawer.getSectionsToRender().map((s) => s.section)) || [];
  };

  async function refresh(sections) {
    const drawer = cartDrawer();
    if (drawer && window.LpCart) await window.LpCart.refreshDrawer(drawer, sections || {});
    else if (window.LpCart) await window.LpCart.updateCartCount();
  }

  /* "No thanks" hides the picker until the cart drawer is next opened. */
  let dismissed = false;
  api.isDismissed = () => dismissed;
  api.resetDismissed = () => {
    dismissed = false;
    document.querySelectorAll('free-gift-picker').forEach((el) => (el.hidden = false));
  };

  class FreeGiftPickerElement extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;
      if (dismissed) this.hidden = true;
      this.confirmButton = this.querySelector('.free-gift-picker__confirm');
      this.errorEl = this.querySelector('.free-gift-picker__error');
      /* Gifts still to choose (from Liquid). With 2+ the inputs are checkboxes, capped here at that number. */
      this.remaining = Math.max(1, Number(this.dataset.remaining) || 1);
      this.addEventListener('change', (event) => {
        if (event.target.name !== 'free-gift-choice') return;
        const inputs = [...this.querySelectorAll('input[name="free-gift-choice"]')];
        let checked = inputs.filter((input) => input.checked);
        if (checked.length > this.remaining) {
          event.target.checked = false;
          checked = checked.filter((input) => input !== event.target);
        }
        if (event.target.type === 'checkbox') {
          const full = checked.length >= this.remaining;
          inputs.forEach((input) => (input.disabled = full && !input.checked));
        }
        if (!this.busy) this.confirmButton.disabled = checked.length === 0;
      });
      this.confirmButton.addEventListener('click', () => this.confirm());
      this.querySelector('.free-gift-picker__skip').addEventListener('click', () => {
        dismissed = true;
        this.hidden = true;
      });
    }

    async confirm() {
      const choices = [...this.querySelectorAll('input[name="free-gift-choice"]:checked')].slice(0, this.remaining);
      if (!choices.length || this.busy) return;
      this.busy = true;
      this.confirmButton.disabled = true;
      this.confirmButton.setAttribute('aria-busy', 'true');
      this.errorEl.hidden = true;
      try {
        const response = await fetch(`${root()}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: choices.map((choice) => ({ id: Number(choice.value), quantity: 1, properties: { [PROPERTY]: 'true' } })),
            sections: drawerSections(),
            sections_url: window.location.pathname,
          }),
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.description || data.message || response.statusText);
        mayHaveGifts = true;
        await refresh(data.sections);
        /* A successful re-render replaces this element; if it is still here, the redraw failed. */
        if (this.isConnected) {
          this.busy = false;
          this.confirmButton.disabled = false;
          this.confirmButton.removeAttribute('aria-busy');
        }
      } catch (error) {
        console.warn('[free-gift-picker] could not add the free gift', error);
        this.errorEl.textContent = error.message || 'Something went wrong. Please try again.';
        this.errorEl.hidden = false;
        this.busy = false;
        this.confirmButton.disabled = false;
        this.confirmButton.removeAttribute('aria-busy');
      }
    }
  }
  if (!customElements.get('free-gift-picker')) customElements.define('free-gift-picker', FreeGiftPickerElement);

  /* The drawer wrapper survives re-renders; it gains `active` each time the drawer opens. */
  const watchDrawerOpen = () => {
    const wrapper = document.querySelector('.cart_drawer_main_wrapper');
    if (!wrapper) return;
    let open = wrapper.classList.contains('active');
    new MutationObserver(() => {
      const nowOpen = wrapper.classList.contains('active');
      if (nowOpen && !open) api.resetDismissed();
      open = nowOpen;
    }).observe(wrapper, { attributes: true, attributeFilter: ['class'] });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', watchDrawerOpen, { once: true });
  else watchDrawerOpen();

  /* Remove picker gifts when no trigger discount is applied (same pattern as assets/lp-cart.js). */
  /* Failed removals are retried on the next page load (in memory only, never per session). */
  const tried = new Set();

  let running = false;
  let queued = false;
  async function removeOrphanedGifts() {
    if (running) {
      queued = true;
      return;
    }
    running = true;
    try {
      const cart = await (await fetch(`${root()}cart.js`, { cache: 'no-store' })).json();
      mayHaveGifts = pickerGiftLines(cart).length > 0;
      if (enabled && attributesToClear(cart)) document.dispatchEvent(new CustomEvent('free-gift-picker:cart-checked'));
      const keys = keysToRemove(cart, rules, tried, nowSeconds());
      /* Picker active: still trim gifts the cart no longer earns (allowance from Liquid). */
      let updates = Object.fromEntries(keys.map((key) => [key, 0]));
      if (!keys.length && pickerGiftLines(cart).length) {
        const allowance = await fetchAllowance();
        if (allowance !== null) excessGiftKeys(cart, allowance, tried).forEach(({ key, quantity }) => (updates[key] = quantity));
      }
      const changed = Object.keys(updates);
      if (!changed.length) return;
      changed.forEach((key) => tried.add(key));

      const onCartPage = Boolean(document.getElementById('main-cart-items'));
      const response = await fetch(`${root()}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          updates,
          sections: onCartPage ? [] : drawerSections(),
          sections_url: window.location.pathname + window.location.search,
        }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || data.message || response.statusText);
      changed.forEach((key) => tried.delete(key));
      document.dispatchEvent(new CustomEvent('free-gift-picker:gifts-removed', { detail: { keys: changed } }));
      if (onCartPage) {
        window.location.reload();
        return;
      }
      await refresh(data.sections);
    } catch (error) {
      console.warn('[free-gift-picker] could not remove free gifts without a trigger discount', error);
    } finally {
      running = false;
      if (queued) {
        queued = false;
        scheduleCheck();
      }
    }
  }
  api.removeOrphanedGifts = removeOrphanedGifts;

  /* Gifts the current cart earns, from sections/free-gift-allowance.liquid; null when it can't be read. */
  async function fetchAllowance() {
    try {
      const html = await (await fetch(`${root()}?section_id=free-gift-allowance`, { cache: 'no-store' })).text();
      const match = html.match(/data-free-gift-allowance="(\d+)"/);
      return match ? Number(match[1]) : null;
    } catch (error) {
      console.warn('[free-gift-picker] could not read the free gift allowance', error);
      return null;
    }
  }

  let timer;
  function scheduleCheck() {
    clearTimeout(timer);
    timer = setTimeout(removeOrphanedGifts, 400);
  }

  if ('PerformanceObserver' in window) {
    try {
      new PerformanceObserver((list) => {
        list.getEntries().forEach((entry) => {
          if (entry.initiatorType !== 'fetch' && entry.initiatorType !== 'xmlhttprequest') return;
          const pathname = new URL(entry.name, window.location.href).pathname;
          if (!CART_MUTATION.test(pathname)) return;
          if (enabled && rearmsWatcher(pathname)) mayHaveGifts = true;
          if (mayHaveGifts) scheduleCheck();
        });
      }).observe({ type: 'resource', buffered: false });
    } catch (error) {
      console.warn('[free-gift-picker] cart change watcher unavailable', error);
    }
  }

  const postAttributes = (attributes) =>
    fetch(`${root()}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes }),
    }).then((response) => {
      if (!response.ok) throw new Error(response.statusText);
    });

  /* On an excluded page: record every product whose quantity goes up while the shopper is here. */
  const onExcludedPage = enabled && excludedPaths.length > 0 && isExcludedPath(window.location.pathname, excludedPaths, root());
  /* Baseline = the cart as Shopify rendered this page, so adds made before this script runs still count. */
  let snapshot = parseCartLines(script && script.dataset.cartLines);
  let trackTimer;
  async function trackExcludedAdds() {
    try {
      const cart = await (await fetch(`${root()}cart.js`, { cache: 'no-store' })).json();
      const added = addedVariants(snapshot, cart);
      snapshot = variantQuantities(cart);
      const value = nextExclusionValue(cart, normalizePath(window.location.pathname, root()), added);
      if (!value) return;
      await postAttributes({ [EXCLUDED_ATTRIBUTE]: value });
      if (pickerGiftLines(cart).length) {
        mayHaveGifts = true;
        scheduleCheck();
      }
    } catch (error) {
      console.warn('[free-gift-picker] could not record products added on an excluded page', error);
    }
  }
  if (onExcludedPage) {
    trackExcludedAdds();
    if ('PerformanceObserver' in window) {
      try {
        new PerformanceObserver((list) => {
          const mutated = list.getEntries().some((entry) => {
            if (entry.initiatorType !== 'fetch' && entry.initiatorType !== 'xmlhttprequest') return false;
            return CART_MUTATION.test(new URL(entry.name, window.location.href).pathname);
          });
          if (!mutated) return;
          clearTimeout(trackTimer);
          trackTimer = setTimeout(trackExcludedAdds, 400);
        }).observe({ type: 'resource', buffered: false });
      } catch (error) {
        console.warn('[free-gift-picker] excluded page watcher unavailable', error);
      }
    }
  }

  /* Off excluded pages: clear a lapsed exclusion so the cart attribute goes away with its products. */
  async function tidyExclusion() {
    if (onExcludedPage) return;
    try {
      const cart = await (await fetch(`${root()}cart.js`, { cache: 'no-store' })).json();
      const clear = attributesToClear(cart);
      if (clear) await postAttributes(clear);
    } catch (error) {
      console.warn('[free-gift-picker] could not clear a lapsed exclusion', error);
    }
  }
  if (enabled && script && script.dataset.hasExclusion === 'true') tidyExclusion();
  document.addEventListener('free-gift-picker:cart-checked', tidyExclusion);

  /*
    Slide-in style (Theme settings > Free gift > Picker style). After an add to cart, render
    sections/free-gift-modal.liquid for this page, work out what the add earned (earnedByAdd), and offer
    that many gifts in <dialog data-free-gift-dialog> (layout/theme.liquid). Other cart changes only
    refresh the snapshot. Gift lines earn nothing, so adding the chosen gifts doesn't reopen it.
  */
  const dialog = document.querySelector('dialog[data-free-gift-dialog]');
  if (enabled && dialog && script && script.dataset.style === 'modal') {
    let units = {};
    try {
      units = JSON.parse(script.dataset.lineUnits || '{}');
    } catch (error) {
      console.warn('[free-gift-picker] could not read the cart line units', error);
    }
    let sawAdd = false;
    let modalTimer;
    let modalRunning = false;
    let modalQueued = false;

    async function checkAdd() {
      if (modalRunning) {
        modalQueued = true;
        return;
      }
      modalRunning = true;
      const wasAdd = sawAdd;
      sawAdd = false;
      try {
        const url = `${window.location.pathname}?section_id=free-gift-modal`;
        const html = await (await fetch(url, { cache: 'no-store' })).text();
        const holder = document.createElement('div');
        holder.innerHTML = html;
        const content = holder.querySelector('[data-free-gift-modal]');
        if (!content) return;
        let after = {};
        try {
          after = JSON.parse(content.dataset.units || '{}');
        } catch (error) {
          return;
        }
        const earned = earnedByAdd(units, after);
        units = after;
        if (!wasAdd || dialog.open || content.dataset.active !== 'true') return;
        const count = modalGiftCount(earned, Number(content.dataset.allowance) || 0, Number(content.dataset.claimed) || 0);
        if (count > 0 && content.querySelector('input[name="free-gift-modal-choice"]')) openModal(content, count);
      } catch (error) {
        console.warn('[free-gift-picker] could not check the add to cart for free gifts', error);
      } finally {
        modalRunning = false;
        if (modalQueued) {
          modalQueued = false;
          checkAdd();
        }
      }
    }

    function openModal(content, count) {
      dialog.replaceChildren(content);
      const inputs = [...content.querySelectorAll('input[name="free-gift-modal-choice"]')];
      const max = Math.min(count, inputs.length);
      const heading = (max > 1 ? content.dataset.headingMany : content.dataset.heading) || '';
      const text = heading.replace(/\[count\]/g, max);
      content.querySelector('[data-fgm-heading]').textContent = text;
      content.querySelector('[data-fgm-legend]').textContent = text;
      const confirmButton = content.querySelector('[data-fgm-confirm]');
      const countEl = content.querySelector('[data-fgm-count]');
      const errorEl = content.querySelector('.fgm__error');
      const update = () => {
        const checked = inputs.filter((input) => input.checked);
        inputs.forEach((input) => (input.disabled = checked.length >= max && !input.checked));
        confirmButton.disabled = checked.length === 0;
        countEl.textContent = max > 1 ? `${checked.length} of ${max} chosen` : '';
      };
      content.addEventListener('change', update);
      update();
      content.querySelectorAll('[data-fgm-close]').forEach((button) => button.addEventListener('click', () => dialog.close()));
      confirmButton.addEventListener('click', async () => {
        const choices = inputs.filter((input) => input.checked).slice(0, max);
        if (!choices.length || confirmButton.getAttribute('aria-busy') === 'true') return;
        confirmButton.disabled = true;
        confirmButton.setAttribute('aria-busy', 'true');
        errorEl.hidden = true;
        try {
          const response = await fetch(`${root()}cart/add.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({
              items: choices.map((choice) => ({ id: Number(choice.value), quantity: 1, properties: { [PROPERTY]: 'true' } })),
              sections: drawerSections(),
              sections_url: window.location.pathname,
            }),
          });
          const data = await response.json();
          if (!response.ok) throw new Error(data.description || data.message || response.statusText);
          mayHaveGifts = true;
          dialog.close();
          await refresh(data.sections);
        } catch (error) {
          console.warn('[free-gift-picker] could not add the free gifts', error);
          errorEl.textContent = error.message || 'Something went wrong. Please try again.';
          errorEl.hidden = false;
          confirmButton.removeAttribute('aria-busy');
          update();
        }
      });
      dialog.showModal();
      content.querySelector('.fgm__back')?.blur();
      inputs[0]?.focus({ preventScroll: true });
    }

    /* Click on the backdrop (outside the panel) closes it, like Back. */
    dialog.addEventListener('click', (event) => {
      if (event.target !== dialog) return;
      const rect = dialog.getBoundingClientRect();
      const inside = event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom;
      if (!inside) dialog.close();
    });

    if ('PerformanceObserver' in window) {
      try {
        new PerformanceObserver((list) => {
          list.getEntries().forEach((entry) => {
            if (entry.initiatorType !== 'fetch' && entry.initiatorType !== 'xmlhttprequest') return;
            const match = new URL(entry.name, window.location.href).pathname.match(CART_MUTATION);
            if (!match) return;
            if (match[1] === 'add') sawAdd = true;
            clearTimeout(modalTimer);
            modalTimer = setTimeout(checkAdd, 450);
          });
        }).observe({ type: 'resource', buffered: false });
      } catch (error) {
        console.warn('[free-gift-picker] add to cart watcher unavailable', error);
      }
    }
  }

  const checkOnLoad = () => mayHaveGifts && scheduleCheck();
  if (document.readyState === 'complete') checkOnLoad();
  else window.addEventListener('load', checkOnLoad, { once: true });
  window.addEventListener('pageshow', (event) => event.persisted && scheduleCheck());
})();
